import React, { useMemo } from "react";

const DataTableSkeleton = React.memo(({ isSmall }) => {
  const skeletonItems = useMemo(() => Array(5).fill(0), []);

  return (
    <div role="status" className="block w-full pb-2">
      {/* Header strip */}
      <div className="mb-1 hidden border-b border-gray-100 bg-gradient-to-l from-purple-50/30 via-gray-50/60 to-transparent px-5 py-3 dark:border-gray-700/60 dark:from-purple-950/20 dark:via-gray-800 lg:flex">
        <div className="flex w-full items-center gap-8">
          <div className="h-3 w-32 rounded-full bg-gray-200/80 dark:bg-gray-700" />
          <div className="h-3 w-24 rounded-full bg-gray-200/80 dark:bg-gray-700" />
          <div className="h-3 w-28 rounded-full bg-gray-200/80 dark:bg-gray-700" />
          <div className="h-3 w-20 rounded-full bg-gray-200/80 dark:bg-gray-700" />
          <div className="ms-auto h-3 w-16 rounded-full bg-gray-200/80 dark:bg-gray-700" />
        </div>
      </div>

      {skeletonItems.map((_, index) => (
        <div
          key={index}
          className="relative flex items-center justify-between overflow-hidden border-b border-gray-100 px-5 py-3.5 last:border-0 dark:border-gray-700/50"
          style={{ animationDelay: `${index * 80}ms` }}
        >
          {/* Shimmer overlay */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 -translate-x-full animate-[tableShimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/5"
          />
          <div className="relative flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800" />
            <div>
              <div className="mb-1.5 h-3.5 w-20 rounded-full bg-gradient-to-r from-gray-200 to-gray-100 dark:from-gray-700 dark:to-gray-800 md:w-48" />
              <div className="h-3 w-16 rounded-full bg-gray-100 dark:bg-gray-800 md:w-36" />
            </div>
          </div>
          {!isSmall && (
            <>
              <div className="relative hidden h-3 w-32 rounded-full bg-gradient-to-r from-gray-200 to-gray-100 dark:from-gray-700 dark:to-gray-800 lg:block" />
              <div className="relative hidden h-3 w-32 rounded-full bg-gradient-to-r from-gray-200 to-gray-100 dark:from-gray-700 dark:to-gray-800 lg:block" />
              <div className="relative hidden h-6 w-20 rounded-full bg-gradient-to-r from-purple-100/50 to-purple-50/30 ring-1 ring-purple-100 dark:from-purple-950/40 dark:to-purple-950/20 dark:ring-purple-800/30 lg:block" />
            </>
          )}
          <div className="relative flex items-center justify-center gap-2 md:gap-3">
            <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 md:h-8 md:w-8" />
            <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 md:h-8 md:w-8" />
            <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 md:h-8 md:w-8" />
          </div>
        </div>
      ))}

      {/* Pagination skeleton */}
      <div className="hidden items-center justify-between gap-3 border-t border-gray-100 px-5 pt-5 dark:border-gray-700/50 lg:flex">
        <div className="h-4 w-48 rounded-full bg-gray-100 dark:bg-gray-800" />
        <div className="flex items-center gap-1">
          <div className="h-8 w-8 rounded-lg bg-gray-100 dark:bg-gray-800" />
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-100 to-purple-50 ring-1 ring-purple-200 dark:from-purple-950/50 dark:to-purple-950/20 dark:ring-purple-800/40" />
          <div className="h-8 w-8 rounded-lg bg-gray-100 dark:bg-gray-800" />
          <div className="h-8 w-8 rounded-lg bg-gray-100 dark:bg-gray-800" />
          <div className="h-8 w-8 rounded-lg bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>

      <style>{`@keyframes tableShimmer { 100% { transform: translateX(100%); } }`}</style>
      <span className="sr-only">Loading...</span>
    </div>
  );
});

export default DataTableSkeleton;
